export interface DepartmentHierarchy {
  raw: string;
  normalized: string;
  headquarter?: string;
  department?: string;
  hierarchy: string[];
  generalHeadquarters: string[];
}

export const UNASSIGNED_HEADQUARTER = 'Unassigned HQ';

const FULL_WIDTH_SPACE_REGEX = /\u3000/g;

function normalizeWhitespace(value: string): string {
  return value
    .replace(FULL_WIDTH_SPACE_REGEX, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Parse a department string into hierarchical components, ignoring tokens that contain "統括本部".
 *
 * This function handles multiple formats:
 * 1. Hierarchical paths separated by "/" (e.g., "本部名 / 部門名")
 * 2. Space-separated hierarchies (e.g., "本部名 部門名")
 * 3. Sentences containing headquarter names ending with "本部" (e.g., "運用保守コンサルティング本部アプリチーム...")
 */
export function parseDepartmentHierarchy(value?: string | null): DepartmentHierarchy {
  if (!value) {
    return {
      raw: '',
      normalized: '',
      hierarchy: [],
      generalHeadquarters: []
    };
  }

  const normalizedRaw = normalizeWhitespace(value);
  if (!normalizedRaw) {
    return {
      raw: '',
      normalized: '',
      hierarchy: [],
      generalHeadquarters: []
    };
  }

  // Try to extract headquarter name from sentence format (e.g., "運用保守コンサルティング本部アプリチーム...")
  // This regex matches Japanese text ending with "本部" (headquarter)
  const headquarterMatch = normalizedRaw.match(/[ぁ-んァ-ヶー一-龠]+本部/);
  if (headquarterMatch && !normalizedRaw.includes('/') && !normalizedRaw.includes('統括本部')) {
    const extractedHQ = headquarterMatch[0];
    // Check if this looks like a sentence (has additional text after the headquarter name)
    const afterHQ = normalizedRaw.substring(normalizedRaw.indexOf(extractedHQ) + extractedHQ.length);
    if (afterHQ.length > 0 && !afterHQ.startsWith(' / ')) {
      // This is a sentence format, use the extracted headquarter
      return {
        raw: normalizedRaw,
        normalized: extractedHQ,
        headquarter: extractedHQ,
        department: undefined,
        hierarchy: [extractedHQ],
        generalHeadquarters: []
      };
    }
  }

  const segments = normalizedRaw
    .split('/')
    .map(segment => normalizeWhitespace(segment))
    .filter(segment => segment.length > 0);

  const hierarchy: string[] = [];
  const generalHQ: string[] = [];

  segments.forEach(segment => {
    if (!segment) return;

    const tokens = segment
      .split(' ')
      .map(token => token.trim())
      .filter(token => token.length > 0);

    const filteredTokens = tokens.filter(token => {
      if (token.includes('統括本部')) {
        generalHQ.push(token);
        return false;
      }
      return true;
    });

    const tokensToUse = filteredTokens.length > 0 ? filteredTokens : tokens;
    if (tokensToUse.length === 0) return;

    const levelLabel = tokensToUse[tokensToUse.length - 1];
    hierarchy.push(levelLabel);
  });

  const dedupedHierarchy: string[] = [];
  const seen = new Set<string>();
  hierarchy.forEach(level => {
    if (level && !seen.has(level)) {
      dedupedHierarchy.push(level);
      seen.add(level);
    }
  });

  let normalized = '';
  if (dedupedHierarchy.length === 0) {
    normalized = normalizedRaw;
  } else if (dedupedHierarchy.length === 1) {
    normalized = dedupedHierarchy[0];
  } else {
    normalized = `${dedupedHierarchy[0]} / ${dedupedHierarchy[dedupedHierarchy.length - 1]}`;
  }

  const headquarter = dedupedHierarchy[0];
  const department = dedupedHierarchy.length > 1
    ? dedupedHierarchy[dedupedHierarchy.length - 1]
    : undefined;

  return {
    raw: normalizedRaw,
    normalized,
    headquarter,
    department,
    hierarchy: dedupedHierarchy,
    generalHeadquarters: generalHQ
  };
}

export const HEADQUARTER_FILTER_PREFIX = 'hq::';
export const DEPARTMENT_FILTER_PREFIX = 'dept::';

export function normalizeDepartmentValue(value?: string | null): string {
  if (!value) return '';
  return normalizeWhitespace(value);
}
